import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Image as ImageIcon, Eye, Film, Twitter, MessagesSquare, HelpCircle, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, showUploadError } from "@/lib/api";
import BoostPricingModal from "@/components/BoostPricingModal";

const STATUS_CHIP = {
  none: ["No media yet", "text-slate-500 border-white/10 bg-slate-500/10"],
  generating: ["Generating…", "text-amber-400 border-amber-500/40 bg-amber-500/10 animate-pulse"],
  pending_approval: ["Pending approval", "text-amber-400 border-amber-500/40 bg-amber-500/10"],
  approved: ["Live on the leaderboard", "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"],
  rejected: ["Rejected", "text-red-400 border-red-500/40 bg-red-500/10"],
  failed: ["Generation failed", "text-red-400 border-red-500/40 bg-red-500/10"],
};

function getVideoDuration(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that video file."));
    };
    video.src = url;
  });
}

const SOCIAL_HANDLE_RE = /^[A-Za-z0-9._-]{1,30}$/;

function normalizeSocialInput(value, platform) {
  const raw = value.trim().replace(/^@+/, "").replace(/\/+$/, "");
  if (!raw) return "";
  const hasUrlShape = /^https?:\/\//i.test(raw) || raw.includes("/");
  const withoutScheme = raw.replace(/^https?:\/\//i, "");
  let parts = withoutScheme.split("/").filter(Boolean);
  if (hasUrlShape && parts.length && parts[0].includes(".")) {
    parts = platform === "reddit" && ["user", "u"].includes(parts[1]?.toLowerCase()) ? parts.slice(2) : parts.slice(1);
  }
  const handle = (parts[0] || "").split("?")[0].split("#")[0].replace(/^@+/, "");
  return SOCIAL_HANDLE_RE.test(handle) ? handle : null;
}


export default function OwnerStudio({ company, onChanged }) {
  const [info, setInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);

  const [socialX, setSocialX] = useState(company.social_x || "");
  const [socialReddit, setSocialReddit] = useState(company.social_reddit || "");
  const [makers, setMakers] = useState(company.makers?.join(", ") || "");
  const [savingSocial, setSavingSocial] = useState(false);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const eligible = company.verified;

  const load = useCallback(() => {
    api.get(`/companies/${company.slug}/gif/status`).then((r) => setInfo(r.data)).catch(() => {});
  }, [company.slug]);

  useEffect(load, [load]);

  useEffect(() => {
    setSocialX(company.social_x || "");
    setSocialReddit(company.social_reddit || "");
    setMakers(company.makers?.join(", ") || "");
  }, [company.social_x, company.social_reddit, company.makers]);


  useEffect(() => {
    if (!info) return;
    const gifReady = info.status !== "generating" && info.status !== "none";
    const videoReady = info.video_status && info.video_status !== "none";
    if (gifReady || videoReady) onChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info?.status, info?.video_status]);

  const saveSocialLinks = async (e) => {
    e.preventDefault();
    const normalizedX = normalizeSocialInput(socialX, "x");
    const normalizedReddit = normalizeSocialInput(socialReddit, "reddit");
    if (normalizedX === null || normalizedReddit === null) {
      toast.error("Social usernames can use letters, numbers, underscores, periods or hyphens — no spaces.");
      return;
    }
    setSavingSocial(true);
    try {
      const makersArr = makers.split(",").map((m) => m.trim()).filter(Boolean);
      await api.patch(`/companies/${company.slug}/profile`, {
        social_x: normalizedX,
        social_reddit: normalizedReddit,
        makers: makersArr,
      });
      setSocialX(normalizedX);
      setSocialReddit(normalizedReddit);
      toast.success("Social links saved.");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSocial(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/companies/${company.slug}/logo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo uploaded.");
      load();
      onChanged();
    } catch (err) {
      showUploadError(err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Clip must be under 5MB.");
      if (videoRef.current) videoRef.current.value = "";
      return;
    }
    setVideoUploading(true);
    try {
      let duration = null;
      try {
        duration = await getVideoDuration(file);
      } catch {
        // Some valid fragmented MP4/MOV files do not expose metadata to browser video elements.
        // The backend ffprobe check remains the source of truth.
      }
      if (duration !== null && (duration < 3 || duration > 15)) {
        throw new Error(`Clip must be 3-15 seconds (yours is ${duration.toFixed(1)}s).`);
      }
      const form = new FormData();
      form.append("file", file);
      await api.post(`/companies/${company.slug}/video-upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Video uploaded — admin approval required before it goes live.");
      load();
      onChanged();
    } catch (err) {
      showUploadError(err);
    } finally {
      setVideoUploading(false);
      if (videoRef.current) videoRef.current.value = "";
    }
  };


  const toggleOptOut = async (optOut) => {
    try {
      await api.post(`/companies/${company.slug}/gif/opt-out`, { opt_out: optOut });
      toast.success(optOut ? "Static logo will be shown instead." : "Top 3 media enabled again.");
      load();
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const status = info?.status || "none";
  const [pendingPaid, setPendingPaid] = useState([]);
  useEffect(() => {
    api.get("/boost/my-paid-submissions")
      .then((r) => setPendingPaid(r.data.items || []))
      .catch(() => setPendingPaid([]));
  }, []);
  const videoStatus = info?.video_status || "none";
  const videoChip = STATUS_CHIP[videoStatus] || STATUS_CHIP.none;
  const hasReviewableMedia = ["approved", "pending_approval"].includes(status) || ["approved", "pending_approval"].includes(videoStatus);

  return (
    <div data-testid="owner-studio" className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.04] p-6 mt-6">
      {pendingPaid.length > 0 && (
        <div
          data-testid="owner-studio-paid-pending"
          className="mb-5 rounded-xl border border-emerald-500/40 bg-emerald-500/[0.08] px-4 py-3"
        >
          <p className="text-xs text-emerald-200/90 leading-relaxed mb-2">
            <span className="font-semibold text-emerald-300">Payment received.</span>{" "}
            Finish your {pendingPaid.length > 1 ? `${pendingPaid.length} paid submissions` : "paid submission"} by adding
            the social post link and video so our team can review it.
          </p>
          {pendingPaid.map((p) => (
            <Link
              key={p.id}
              to={`/boost/confirm?submission=${p.id}`}
              data-testid={`owner-studio-finish-submission-${p.id}`}
              className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-bold rounded-full px-4 py-2 mr-2 mt-1 transition-colors"
            >
              Submit content for {p.tier === "pro_boost" ? "Pro Boost" : p.tier === "boost" ? "Boost" : "Discover Boost"}
            </Link>
          ))}
        </div>
      )}
      {company.ownership_status === "claim_pending" && (
        <div
          data-testid="owner-studio-claim-pending"
          className="mb-5 flex items-start gap-2.5 rounded-xl border border-amber-400/70 bg-amber-900/50 px-4 py-3"
        >
          <HelpCircle size={15} className="text-amber-400 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-100 leading-relaxed">
            <span className="font-semibold text-amber-400">Draft mode — claim under review.</span>{" "}
            You can edit your listing details now. Your ownership badge, media uploads and boost placements unlock once an admin approves your claim.
          </p>
        </div>
      )}
      <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-emerald-400 mb-1">{"// Owner studio"}</p>
      <h2 className="font-heading text-lg font-bold text-slate-100 mb-5">Your Top 3 media slot</h2>

      <div className="flex items-center gap-4 flex-wrap mb-5">
        <div className="w-24 aspect-video rounded-lg border border-white/10 bg-[#0A0A09] flex items-center justify-center overflow-hidden">
          {info?.logo_url ? (
            <img src={info.logo_url} alt={`${company.name} logo`} className="w-full h-full object-contain p-1" />
          ) : (
            <ImageIcon size={18} className="text-slate-600" />
          )}
        </div>
        <div className="flex-1 min-w-52">
          <p className="text-sm text-slate-300 font-semibold mb-1">Logo</p>
          <p className="text-xs text-slate-500 mb-3">
            PNG or SVG preferred. JPG works but is auto-converted and can show hard edges on the dark slot.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".png,.svg,.jpg,.jpeg"
            data-testid="logo-upload-input"
            onChange={uploadLogo}
            disabled={uploading}
            className="hidden"
            id="logo-upload"
          />
          <label
            htmlFor="logo-upload"
            data-testid="logo-upload-button"
            className={`inline-flex items-center gap-2 border border-white/20 hover:border-emerald-500/50 text-slate-200 hover:text-emerald-400 font-semibold text-xs rounded-full px-4 py-2 cursor-pointer transition-all duration-200 ${uploading ? "opacity-50 pointer-events-none" : ""}`}
          >
            <Upload size={13} /> {uploading ? "Uploading…" : info?.logo_url ? "Replace logo" : "Upload logo"}
          </label>
        </div>
      </div>

      <form data-testid="social-links-form" onSubmit={saveSocialLinks} className="border-t border-white/5 pt-5 mb-5">
        <p className="text-sm text-slate-300 font-semibold mb-1">Social & Team</p>
        <p className="text-xs text-slate-500 mb-4">Social usernames are auto-converted from full URLs. Add your makers below.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-300">X</span>
            <span className="mt-1.5 flex items-center gap-2 rounded-lg border border-white/10 bg-[#0A0A09] px-3 py-2.5 focus-within:border-emerald-500/50 transition-colors">
              <Twitter size={14} className="text-slate-500 shrink-0" />
              <input
                data-testid="social-x-input"
                value={socialX}
                onChange={(e) => setSocialX(e.target.value)}
                placeholder="username, not full URL"
                maxLength={200}
                className="w-full min-w-0 bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600"
              />
            </span>
          </label>
          <label className="block">
            <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-300">Reddit</span>
            <span className="mt-1.5 flex items-center gap-2 rounded-lg border border-white/10 bg-[#0A0A09] px-3 py-2.5 focus-within:border-emerald-500/50 transition-colors">
              <MessagesSquare size={14} className="text-slate-500 shrink-0" />
              <input
                data-testid="social-reddit-input"
                value={socialReddit}
                onChange={(e) => setSocialReddit(e.target.value)}
                placeholder="username, not full URL"
                maxLength={200}
                className="w-full min-w-0 bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600"
              />
            </span>
          </label>
        </div>
        <label className="block mt-3">
          <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-300">Makers</span>
          <span className="mt-1.5 flex items-center gap-2 rounded-lg border border-white/10 bg-[#0A0A09] px-3 py-2.5 focus-within:border-violet-500/50 transition-colors">
            <input
              data-testid="makers-input"
              value={makers}
              onChange={(e) => setMakers(e.target.value)}
              placeholder="e.g. Alice, Bob, Charlie"
              maxLength={300}
              className="w-full min-w-0 bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600"
            />
          </span>
          <p className="text-[10px] text-slate-600 mt-1">Comma-separated names of the people behind this product.</p>
        </label>
        <button
          type="submit"
          data-testid="social-links-save-button"
          disabled={savingSocial}
          className="mt-4 inline-flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 disabled:opacity-50 text-emerald-400 font-bold text-xs rounded-full px-4 py-2 transition-colors"
        >
          {savingSocial ? "Saving…" : "Save profile"}
        </button>
      </form>


      {eligible ? (
        <div className="space-y-5 border-t border-white/5 pt-5">

          <div className="border-t border-white/5 pt-5">
            <div className="flex items-center gap-3 flex-wrap mb-3">
              <p className="text-sm text-slate-300 font-semibold">Upload your own clip</p>
              <span
                data-testid="video-status-chip"
                className={`font-mono2 text-[10px] uppercase tracking-widest rounded-md px-2.5 py-1 border ${videoChip[1]}`}
              >
                {videoChip[0]}
              </span>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              MP4, WebM or MOV · 3–15 seconds · under 5MB. Admin approval is required before it goes live.
            </p>

            {info?.video_url && (
              <div className="mb-4">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-600 mb-2 flex items-center gap-1.5">
                  <Eye size={11} /> Video preview
                </p>
                <video
                  data-testid="video-preview"
                  controls
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="w-56 aspect-video rounded-lg border border-amber-500/40 bg-black object-cover"
                >
                  {info.video_webm && <source src={info.video_webm} type="video/webm" />}
                  <source src={info.video_url} type="video/mp4" />
                </video>
              </div>
            )}
            {videoStatus === "rejected" && info?.video_reject_reason && (
              <p data-testid="video-reject-reason" className="text-xs text-red-400 mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2">
                Rejected: {info.video_reject_reason}
              </p>
            )}

            {videoStatus === "approved" ? (
              <button
                onClick={() => setShowBoostModal(true)}
                data-testid="video-replace-pay-button"
                className="inline-flex items-center gap-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/40 text-amber-400 font-bold text-sm rounded-full px-5 py-2.5 cursor-pointer transition-colors duration-200"
              >
                <Film size={14} /> Replace video via Boost
              </button>
            ) : (
              <>
                <input
                  ref={videoRef}
                  type="file"
                  accept=".mp4,.webm,.mov,video/mp4,video/webm,video/quicktime"
                  data-testid="video-upload-input"
                  onChange={uploadVideo}
                  disabled={videoUploading}
                  className="hidden"
                  id="video-upload"
                />
                <label
                  htmlFor="video-upload"
                  data-testid="video-upload-button"
                  className={`inline-flex items-center gap-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/40 text-amber-400 font-bold text-sm rounded-full px-5 py-2.5 cursor-pointer transition-colors duration-200 ${videoUploading ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <Film size={14} /> {videoUploading ? "Uploading…" : "Upload video"}
                </label>
              </>
            )}
            {videoStatus === "approved" && (
              <p className="text-[11px] text-slate-500 mt-2">
                Your video is live. Replacing it requires a new Boost submission.
              </p>
            )}
          </div>

          {info?.logo_url && hasReviewableMedia && (
            <label className="inline-flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="gif-optout-toggle"
                checked={!!info?.opt_out}
                onChange={(e) => toggleOptOut(e.target.checked)}
                className="accent-emerald-500 w-4 h-4"
              />
              Keep static logo instead
            </label>
          )}
        </div>
      ) : (
        <p data-testid="gif-locked-note" className="border-t border-white/5 pt-5 text-xs text-slate-500 leading-relaxed">
          Logo GIF generation and video upload unlock once <span className="text-slate-300">{company.name}</span> is{" "}
          <span className="text-emerald-400 font-semibold">Verified</span> by the HighRank team.
          The Verified badge is granted by an admin — reach out if your listing is ready for review.
        </p>
      )}

      {/* ── Amendment Request / Help ───────────────────────────────── */}
      <AmendmentRequestForm slug={company.slug} companyName={company.name} />

      <BoostPricingModal
        open={showBoostModal}
        onOpenChange={setShowBoostModal}
        company={company}
      />
    </div>
  );
}


const AMENDMENT_FIELDS = [
  { value: "", label: "Select what to change" },
  { value: "about", label: "About / Description" },
  { value: "tagline", label: "Tagline" },
  { value: "website", label: "Website URL" },
  { value: "category", label: "Category" },
  { value: "region", label: "Region" },
  { value: "logo", label: "Logo" },
  { value: "social_handles", label: "Social Handles" },
  { value: "other", label: "Other" },
];

function AmendmentRequestForm({ slug, companyName }) {
  const [open, setOpen] = useState(false);
  const [field, setField] = useState("");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!details.trim()) { toast.error("Please describe the change you need."); return; }
    setSending(true);
    try {
      const res = await api.post(`/api/companies/${slug}/amendment-request`, { field, details });
      setSubmitted(res.data.ticket);
      toast.success(`Request submitted — ${res.data.ticket}`);
      setField("");
      setDetails("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <div className="border-t border-white/5 pt-6 mt-2">
        <button
          data-testid="amendment-request-toggle"
          onClick={() => setOpen(true)}
          className="flex items-center gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-xs font-semibold text-amber-300 hover:bg-amber-500/20 hover:border-amber-400/50 transition-all duration-200 group w-full"
        >
          <HelpCircle size={16} className="text-amber-400 shrink-0" />
          <span className="text-black">Need to update your listing? <span className="underline underline-offset-2 decoration-amber-500/40 group-hover:decoration-amber-400">Request an amendment</span></span>
        </button>
      </div>
    );
  }

  return (
    <div data-testid="amendment-request-section" className="border-t border-white/5 pt-6 mt-2">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <HelpCircle size={14} className="text-emerald-400" />
          <h3 className="font-heading text-sm font-semibold text-slate-200 uppercase tracking-wider">Request Amendment</h3>
        </div>
        <button
          data-testid="amendment-request-close"
          onClick={() => { setOpen(false); setSubmitted(null); }}
          className="text-[10px] font-mono2 uppercase tracking-wider text-slate-600 hover:text-slate-300 transition-colors"
        >
          Close
        </button>
      </div>

      {submitted ? (
        <div data-testid="amendment-request-success" className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5 text-center">
          <CheckCircle2 size={28} className="text-emerald-400 mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-200 mb-1">Request Submitted</p>
          <p className="text-xs text-slate-400 mb-3">
            Your ticket <span className="text-emerald-400 font-mono2 font-bold">{submitted}</span> has been sent to our team.
          </p>
          <p className="text-[11px] text-slate-500">We'll review your request and get back to you at the email on file.</p>
          <button
            data-testid="amendment-request-new"
            onClick={() => setSubmitted(null)}
            className="mt-4 text-xs text-emerald-400 hover:text-emerald-300 font-semibold transition-colors"
          >
            Submit another request
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <p className="text-xs text-slate-500 leading-relaxed">
            Can't edit something yourself? Tell us what needs changing on <span className="text-slate-300 font-medium">{companyName}</span> and we'll handle it.
          </p>
          <div>
            <label className="block font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-300 mb-1.5">What to change</label>
            <select
              data-testid="amendment-field-select"
              value={field}
              onChange={(e) => setField(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-[#0A0A09] px-3 py-2 text-sm text-slate-200 outline-none focus:border-emerald-500/50 transition-colors"
            >
              {AMENDMENT_FIELDS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-300 mb-1.5">Details <span className="text-rose-400">*</span></label>
            <textarea
              data-testid="amendment-details-input"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              required
              rows={3}
              placeholder="Describe the change you need — e.g. 'Update description to: We build AI tools for…'"
              className="w-full rounded-lg border border-white/10 bg-[#0A0A09] px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 outline-none focus:border-emerald-500/50 transition-colors resize-none"
            />
          </div>
          <button
            type="submit"
            data-testid="amendment-submit-btn"
            disabled={sending || !details.trim()}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold text-xs px-5 py-2 transition-all duration-200"
          >
            <Send size={12} />
            {sending ? "Sending…" : "Submit Request"}
          </button>
        </form>
      )}
    </div>
  );
}
